import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  syncUser,
  syncCatch,
  patchCatch,
  fetchUserCatches,
  fetchUserProfile,
  syncReview,
  syncDrop,
  patchDrop,
  deleteDropRow,
  fetchDrops,
  fetchDropByCode,
  syncRecurringQuestions,
  fetchRecurringQuestions,
  syncPartnerCodes,
  fetchPartnerCodes,
} from './supabase'
import { notifyDataChanged } from './liveSync'

/**
 * A configurable question, used both for a drop's own questions and for the
 * extra questions recurring (3+ catch) users are asked.
 */
export interface DropQuestion {
  id: string
  label: string
  /** 'text' renders a free-text input; 'choice' renders selectable chips. */
  type: 'text' | 'choice'
  /** Options for a 'choice' question. */
  options?: string[]
}

export interface DropProduct {
  name: string
  brand: string
  /** Optional per-product logo (auto-derived from the brand when omitted). */
  logo?: string
}

/**
 * Audience targeting for a drop. Every field is an OPTIONAL allow-list — an
 * empty/absent field means "no restriction on this dimension". A user must
 * match EVERY populated dimension to see the drop. `minLevel` gates by the
 * playful level name; `minPoints` is the raw threshold (kept in sync so old
 * `requiredPoints` logic keeps working).
 */
export interface DropTargeting {
  ageRanges?: string[]
  interests?: string[]
  occupations?: string[]
  cities?: string[]
  /** Activity level by lifetime catch count: 'new' (1) | 'returning' (2-3) | 'loyal' (4+). */
  activityLevels?: ('new' | 'returning' | 'loyal')[]
  /** Minimum playful level name required to see the drop. */
  minLevel?: string
}

/**
 * How a drop repeats. 'none' = a one-off drop on its start date. Recurring
 * drops repeat from their start `date` every `interval` units of `frequency`,
 * optionally until `until` (YYYY-MM-DD). `weekdays` (0=Sun … 6=Sat) further
 * restricts a weekly recurrence to specific days.
 */
export type RecurrenceFrequency = 'none' | 'daily' | 'weekly' | 'monthly'

export interface DropRecurrence {
  frequency: RecurrenceFrequency
  /** Repeat every N units of the frequency (defaults to 1). */
  interval?: number
  /** Optional end date (YYYY-MM-DD) — no occurrences after this. */
  until?: string
  /** For weekly recurrence: which weekdays repeat (0=Sun … 6=Sat). */
  weekdays?: number[]
}

export const RECURRENCE_FREQUENCIES: {
  value: RecurrenceFrequency
  label: string
}[] = [
  { value: 'none', label: 'One-off (no repeat)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Normalize a stored recurrence value into a valid DropRecurrence. */
export const normalizeRecurrence = (v: unknown): DropRecurrence => {
  if (!v || typeof v !== 'object' || Array.isArray(v))
    return { frequency: 'none' }
  const obj = v as Record<string, unknown>
  const freq = obj.frequency
  const frequency: RecurrenceFrequency =
    freq === 'daily' || freq === 'weekly' || freq === 'monthly' ? freq : 'none'
  const interval = Math.max(1, Number(obj.interval) || 1)
  const until =
    typeof obj.until === 'string' && obj.until.trim() ? obj.until : undefined
  const weekdays = Array.isArray(obj.weekdays)
    ? obj.weekdays
        .map((d) => Number(d))
        .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    : undefined
  return { frequency, interval, until, weekdays }
}

/** Whether a recurrence actually repeats (anything other than 'none'). */
export const isRecurring = (r?: DropRecurrence): boolean =>
  !!r && r.frequency !== 'none'

/** A short human label for a recurrence, e.g. "Every 2 weeks · Mon, Thu". */
export const describeRecurrence = (r?: DropRecurrence): string => {
  if (!r || r.frequency === 'none') return 'One-off'
  const n = Math.max(1, r.interval || 1)
  const unit =
    r.frequency === 'daily'
      ? 'day'
      : r.frequency === 'weekly'
        ? 'week'
        : 'month'
  const base = n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`
  if (r.frequency === 'weekly' && r.weekdays && r.weekdays.length > 0) {
    const days = [...r.weekdays]
      .sort((a, b) => a - b)
      .map((d) => WEEKDAY_LABELS[d])
      .join(', ')
    return `${base} · ${days}`
  }
  return base
}

/**
 * Compute the next occurrence date (YYYY-MM-DD) for a recurring drop on/after
 * `from`. Returns null when the drop is one-off or its recurrence has ended.
 * Scans forward day-by-day up to ~2 years so it never loops forever.
 */
export const nextOccurrence = (
  event: Pick<Event, 'date' | 'recurrence'>,
  from: Date = new Date(),
): string | null => {
  const r = normalizeRecurrence(event.recurrence)
  if (r.frequency === 'none' || !event.date) return null
  const start = new Date(`${event.date}T00:00:00`)
  if (Number.isNaN(start.getTime())) return null
  const fromDay = new Date(from)
  fromDay.setHours(0, 0, 0, 0)
  const until = r.until ? new Date(`${r.until}T23:59:59`) : null
  const interval = Math.max(1, r.interval || 1)
  const fmt = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const cursor = new Date(start)
  // Walk forward up to ~730 days from the later of (start, fromDay).
  for (let i = 0; i < 1100; i++) {
    if (cursor.getTime() >= fromDay.getTime()) {
      if (until && cursor.getTime() > until.getTime()) return null
      if (r.frequency === 'weekly' && r.weekdays && r.weekdays.length > 0) {
        if (r.weekdays.includes(cursor.getDay())) return fmt(cursor)
        cursor.setDate(cursor.getDate() + 1)
        continue
      }
      return fmt(cursor)
    }
    // Advance by one recurrence step.
    if (r.frequency === 'daily') cursor.setDate(cursor.getDate() + interval)
    else if (r.frequency === 'weekly') {
      if (r.weekdays && r.weekdays.length > 0)
        cursor.setDate(cursor.getDate() + 1)
      else cursor.setDate(cursor.getDate() + 7 * interval)
    } else cursor.setMonth(cursor.getMonth() + interval)
  }
  return null
}

/** How many boxes of a drop have been redeemed (total minus remaining). */
export const redeemedCount = (
  event: Pick<Event, 'totalBoxes' | 'boxesLeft'>,
): number => Math.max(0, (event.totalBoxes || 0) - (event.boxesLeft || 0))

/**
 * How users are allowed to redeem a drop:
 *   'qr'   → QR Scan Only (the manual drop-code field is hidden for users)
 *   'code' → Drop Code Only (no QR redemption path offered)
 *   'both' → QR Scan or Drop Code (either works)
 */
export type VerificationMethod = 'qr' | 'code' | 'both'

export const VERIFICATION_METHODS: {
  value: VerificationMethod
  label: string
}[] = [
  { value: 'qr', label: 'QR Scan Only' },
  { value: 'code', label: 'Drop Code Only' },
  { value: 'both', label: 'QR Scan or Drop Code' },
]

/** Normalize any stored value into a valid VerificationMethod (default 'both'). */
export const normalizeVerificationMethod = (v: unknown): VerificationMethod =>
  v === 'qr' || v === 'code' || v === 'both' ? v : 'both'

/** Whether a drop allows redeeming by entering its drop code. */
export const allowsCodeRedemption = (m?: VerificationMethod): boolean =>
  normalizeVerificationMethod(m) !== 'qr'

/** Whether a drop allows redeeming by scanning a QR. */
export const allowsQrRedemption = (m?: VerificationMethod): boolean =>
  normalizeVerificationMethod(m) !== 'code'

export interface Event {
  id: string
  /**
   * Human-readable code that uniquely names this drop. Admins look a drop up by
   * this code to see its exact date, brand and the participants who joined.
   */
  code: string
  title: string
  brand: string
  /**
   * Campaign this drop belongs to (e.g. brand "NOCCO", campaign "Summer Energy
   * Drop"). Stored permanently with the drop so brands can be scoped to only
   * their own campaigns. Falls back to the title when not set.
   */
  campaign?: string
  /** How users may redeem this drop (QR, code, or either). Defaults to 'both'. */
  verificationMethod?: VerificationMethod
  description: string
  /** Start date (YYYY-MM-DD). For recurring drops this is the first occurrence. */
  date: string
  time: string
  address: string
  /**
   * Master availability switch. When `false` the drop is turned OFF and is never
   * collectable regardless of its date/time window. When `true` or undefined the
   * drop follows its normal schedule. Toggled with one click in the admin.
   */
  enabled?: boolean
  boxesLeft: number
  totalBoxes: number
  theme: string
  type: 'walk-in' | 'reservation'
  /** How the drop repeats. Absent/`{frequency:'none'}` = a one-off drop. */
  recurrence?: DropRecurrence
  requiredPoints: number
  /** Audience targeting (who can see this drop). Empty fields = no restriction. */
  targeting?: DropTargeting
  image?: string
  /** Admin-editable questions asked when a user catches this specific drop. */
  customQuestions?: DropQuestion[]
  /** Up to 4 products included in this drop's catch. */
  products?: DropProduct[]
  /** Sponsoring brand assets surfaced to users (makes a drop "sponsored media"). */
  brandLogo?: string
  instagramUrl?: string
  websiteUrl?: string
}

/**
 * Playful, fishing-themed levels with intentionally LOW, easy-to-reach point
 * thresholds (a single review already gets you to the second tier). Ordered
 * low → high. `levelForPoints` walks this to resolve a user's current tier.
 */
export const LEVELS: { name: string; min: number; emoji: string }[] = [
  { name: 'Lil Minnow', min: 0, emoji: '🐟' },
  { name: 'Hooked', min: 30, emoji: '🎣' },
  { name: 'Reel Rookie', min: 75, emoji: '🪝' },
  { name: 'Big Catch', min: 150, emoji: '🐠' },
  { name: 'Master Angler', min: 250, emoji: '🦈' },
]

/**
 * Points needed to unlock the Virtual Fishing game. The game is NOT finished
 * yet, so it is intentionally gated behind the HIGHEST level (Master Angler) —
 * practically nobody reaches it during the current rounds, keeping it locked.
 */
export const GAME_UNLOCK_POINTS = LEVELS[LEVELS.length - 1].min

/** The level name that unlocks the Virtual Fishing game (the highest tier). */
export const GAME_UNLOCK_LEVEL = LEVELS[LEVELS.length - 1].name

/** Resolve the playful level tier (name + emoji) for a given points total. */
export const levelMetaForPoints = (points: number) => {
  let meta = LEVELS[0]
  for (const lvl of LEVELS) if (points >= lvl.min) meta = lvl
  return meta
}

/** Resolve the playful level name for a given points total. */
export const levelForPoints = (points: number): string =>
  levelMetaForPoints(points).name

/** The next tier above the user's current points, or null if maxed out. */
export const nextLevelForPoints = (points: number) =>
  LEVELS.find((l) => l.min > points) ?? null

/** Minimum points needed to reach a given level name (0 if unknown). */
export const pointsForLevel = (levelName: string): number =>
  LEVELS.find((l) => l.name === levelName)?.min ?? 0

/** All level names, low → high — handy for admin level pickers. */
export const LEVEL_NAMES = LEVELS.map((l) => l.name)

/**
 * Activity tier from lifetime catch count:
 *   1 → 'new', 2-3 → 'returning', 4+ → 'loyal'.
 */
export const activityLevelForCatchCount = (
  catchCount: number,
): 'new' | 'returning' | 'loyal' =>
  catchCount >= 4 ? 'loyal' : catchCount >= 2 ? 'returning' : 'new'

/**
 * Whether a user is in a drop's target audience. An empty/absent targeting
 * dimension imposes no restriction; a populated one must be satisfied. Returns
 * true for drops with no targeting at all (visible to everyone).
 */
export const matchesTargeting = (
  targeting: DropTargeting | undefined,
  user: {
    points?: number
    ageRange?: string
    city?: string
    occupation?: string
    interests?: string[]
  } | null,
  catchCount = 1,
): boolean => {
  if (!targeting) return true
  if (!user) return true
  const inList = (list: string[] | undefined, value?: string) =>
    !list || list.length === 0 || (!!value && list.includes(value))
  if (!inList(targeting.ageRanges, user.ageRange)) return false
  if (!inList(targeting.occupations, user.occupation)) return false
  if (!inList(targeting.cities, user.city)) return false
  if (targeting.interests && targeting.interests.length > 0) {
    const owned = user.interests || []
    if (!targeting.interests.some((i) => owned.includes(i))) return false
  }
  if (targeting.activityLevels && targeting.activityLevels.length > 0) {
    if (
      !targeting.activityLevels.includes(activityLevelForCatchCount(catchCount))
    )
      return false
  }
  if (targeting.minLevel) {
    if ((user.points ?? 0) < pointsForLevel(targeting.minLevel)) return false
  }
  return true
}

/** Whether a user has reached Fisher status (the fishing game is unlocked). */
export const isGameUnlocked = (points: number): boolean =>
  points >= GAME_UNLOCK_POINTS

/**
 * Gone Fishing's official Instagram. Surfaced across the consumer journey so
 * people can follow and stay up to date on new drops.
 */
export const INSTAGRAM_URL =
  'https://www.instagram.com/gonefishing.app?igsh=YnU5YmYwbTI1aGpo&utm_source=qr'

/** Points awarded (once) for following Gone Fishing on Instagram. */
export const INSTAGRAM_FOLLOW_POINTS = 10

/**
 * Flags we persist on a user without needing extra DB columns: they live inside
 * the existing `customAnswers` jsonb bag (which already merges + round-trips
 * through Supabase), keyed with reserved `__`-prefixed ids so they never clash
 * with real question answers.
 */
export const IG_FOLLOWED_KEY = '__igFollowed'
export const NOTIFY_OPTIN_KEY = '__notifyOptIn'

/** Whether this user has already claimed the Instagram-follow reward. */
export const hasFollowedInstagram = (
  user: Pick<User, 'customAnswers'> | null | undefined,
): boolean => user?.customAnswers?.[IG_FOLLOWED_KEY] === 'true'

/** Whether this user opted in to new-drop notifications. */
export const hasNotificationOptIn = (
  user: Pick<User, 'customAnswers'> | null | undefined,
): boolean => user?.customAnswers?.[NOTIFY_OPTIN_KEY] === 'true'

export interface User {
  phone: string
  name: string
  ageRange?: string
  city?: string
  interests?: string[]
  storePreference?: string
  purchaseFrequency?: string
  lifestyle?: string
  points: number
  /** Playful level name — see LEVELS. Derived from points via levelForPoints. */
  level: string
  eligible_for_next_event: boolean
  occupation?: string
  /** Deeper profile collected progressively on later catches. */
  profession?: string
  workArea?: string
  favoriteArea?: string
  /**
   * Free-form answers to admin-configured questions (per-drop custom questions
   * and the recurring-user extra questions), keyed by question id.
   */
  customAnswers?: Record<string, string>
  /**
   * Referral code (normalized phone) of the user who invited this person, if
   * they joined via an invite link (?ref=...). Powers referral analytics.
   */
  invitedBy?: string
  /**
   * Partner code (owned by a brand/event partner) this user entered when they
   * joined — e.g. a code a partner hands out when inviting people to our drops.
   * Lets us attribute & verify which partner drove which sign-ups.
   */
  partnerCode?: string
}

/**
 * A code we issue to a partner (a brand, event, or — most commonly — a physical
 * distribution location/store like "7-Eleven – Drottninggatan 56"). Users enter
 * it on registration so we can attribute and verify who each partner brought in.
 *
 * Each partner code also IS that partner's identity in the Partner Portal: they
 * sign in with this code and see only the drops + redemptions at their store,
 * because redemptions are what define their payout.
 */
export interface PartnerCode {
  code: string
  name: string
  type: 'brand' | 'event'
  active: boolean
  /** Street address of the partner's physical location (e.g. "Drottninggatan 56"). */
  address?: string
  /** City of the location (e.g. "Stockholm"). */
  city?: string
  /** Postal code of the location (e.g. "111 20"). */
  postalCode?: string
}

/** A partner's full one-line location label, e.g. "Drottninggatan 56, Stockholm, 111 20". */
export const partnerLocationLabel = (
  p: Pick<PartnerCode, 'address' | 'city' | 'postalCode'>,
): string =>
  [p.address, p.city, p.postalCode]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(', ')

export interface RegisteredUser extends User {
  registeredAt: number
  lastSeenAt: number
  /** How many times this phone has caught a box. 1 = first catch, 2+ = returning. */
  catchCount: number
  occupation?: string
}

/**
 * Unique code per sampling round/batch. Every catch & reservation is tagged
 * with the active batch code so all data can be sliced by round.
 */
export interface BatchCode {
  code: string
  round: number
  label: string
  boxes: number
}

export const BATCH_CODES: BatchCode[] = [
  {
    code: 'FISKE-R1-A7K2',
    round: 1,
    label: 'Proof of Attraction',
    boxes: 1000,
  },
  { code: 'FISKE-R2-M4P9', round: 2, label: 'Proof of Movement', boxes: 2000 },
  { code: 'FISKE-R3-T8X5', round: 3, label: 'Proof of Retention', boxes: 3000 },
  {
    code: 'FISKE-R4-N3W6',
    round: 4,
    label: 'Proof of Network Effect',
    boxes: 4000,
  },
  { code: 'FISKE-R5-C9V1', round: 5, label: 'Sampling Club', boxes: 0 },
]

/** Strip spaces so phone numbers match regardless of formatting. */
export const normalizePhone = (phone: string) => phone.replace(/\s/g, '')

export interface Reservation {
  id: string
  eventId: string
  userId: string
  status: 'reserved' | 'redeemed' | 'expired' | 'reviewed'
  timestamp: number
  userCode: string
  staffCode: string
  /** Which sampling round/batch this catch belongs to. */
  batchCode: string
  /**
   * True once this reservation was converted into a real catch at pickup by a
   * returning user entering the drop code — i.e. a recurring user taking their
   * reserved spot (as opposed to a fresh walk-in catch).
   */
  isRecurring?: boolean
}

export interface ReviewEntry {
  id: string
  reservationId: string
  userId: string
  productName: string
  productBrand: string
  taste: boolean | null
  packaging: boolean | null
  buyAgain: boolean | null
  timestamp: number
}

interface AppState {
  user: User | null
  registeredUsers: RegisteredUser[]
  events: Event[]
  reservations: Reservation[]
  reviews: ReviewEntry[]
  /**
   * Up to 3 extra questions asked ONLY to recurring users (4th catch onward).
   * Users under 3 catches keep answering the profile-completion questions.
   */
  recurringQuestions: DropQuestion[]
  /** Codes issued to brand/event partners for inviting people to our drops. */
  partnerCodes: PartnerCode[]
  activeEventId: string | null
  activeBatchCode: string
  /**
   * Set when a drop write (create/update/delete) to Supabase is rejected — e.g.
   * Row Level Security is blocking anon writes. The admin surfaces this so the
   * user knows their changes aren't persisting instead of silently losing them.
   */
  dropWriteError: string | null
  clearDropWriteError: () => void
  /**
   * Session tombstones: ids the admin deleted this session. hydrateDrops honors
   * them so a delete that failed to persist server-side doesn't "come back" on
   * the next refresh within the session.
   */
  deletedDropIds: string[]
  setUser: (user: User, isReturning?: boolean) => void
  updateUser: (updates: Partial<User>) => void
  /**
   * Award the one-time Instagram-follow reward (10 pts). No-ops (returns false)
   * when there's no user or the reward was already claimed; otherwise awards +
   * persists the points and marks the user as a follower. Returns whether the
   * reward was granted this call.
   */
  followInstagram: () => boolean
  /** Opt this user in/out of new-drop notifications (persisted to the backend). */
  setNotificationOptIn: (optIn: boolean) => void
  setActiveEventId: (id: string | null) => void
  setActiveBatchCode: (code: string) => void
  addReservation: (
    eventId: string,
    status?: 'reserved' | 'redeemed',
    isRecurring?: boolean,
  ) => void
  redeemReservation: (reservationId: string) => void
  /**
   * Convert a reserved spot into a real catch at pickup. Succeeds ONLY when
   * `code` matches the reserved drop's admin code; marks the catch as recurring
   * and syncs the status + recurring flag to Supabase. Returns whether the
   * code was valid.
   */
  redeemReservationByCode: (reservationId: string, code: string) => boolean
  completeReview: (reservationId: string) => void
  addReviewEntry: (entry: Omit<ReviewEntry, 'id' | 'timestamp'>) => void
  addEvent: (event: Event) => void
  updateEvent: (id: string, updates: Partial<Event>) => void
  /**
   * Refill a drop by adding `amount` boxes to BOTH its total capacity and the
   * remaining count (a restock — redeemed boxes are untouched). Syncs the new
   * totals to Supabase so every device sees the refill in real time.
   */
  refillDrop: (id: string, amount: number) => void
  deleteEvent: (id: string) => void
  /** Replace the local drop list with the shared backend's drops. */
  setEvents: (events: Event[]) => void
  /** Pull the latest drops from the shared backend (every device). */
  hydrateDrops: () => Promise<void>
  /**
   * Resolve a drop by its code so the code-entry flow ALWAYS works for any
   * valid code: checks the local list first, then does a live backend read (a
   * read succeeds even when writes are denied) and merges the result in.
   */
  resolveDropByCode: (code: string) => Promise<Event | null>
  /** Replace the recurring-user extra questions (max 3) and sync to backend. */
  setRecurringQuestions: (questions: DropQuestion[]) => void
  /** Pull the latest recurring-user questions from the shared backend. */
  hydrateRecurringQuestions: () => Promise<void>
  /** Replace the partner codes and sync to the shared backend. */
  setPartnerCodes: (codes: PartnerCode[]) => void
  /** Pull the latest partner codes from the shared backend (every device). */
  hydratePartnerCodes: () => Promise<void>
  /** Pull the user's latest reservations from the shared backend. */
  hydrateUserReservations: () => Promise<void>
  /**
   * Restore a returning user's SAVED profile (points, level, catch count) AND
   * their full catch + review history from the shared backend, by phone. Called
   * on login/registration so activity comes back on any device — even a fresh
   * one with an empty local store.
   */
  hydrateUserByPhone: (phone: string) => Promise<void>
  resetStore: () => void
}

/** Map a store Event to the Supabase drop payload. */
const eventToSheetDrop = (e: Event, active: boolean) => ({
  id: e.id,
  code: e.code,
  title: e.title,
  brand: e.brand,
  campaign: e.campaign || e.title,
  verificationMethod: normalizeVerificationMethod(e.verificationMethod),
  description: e.description,
  date: e.date,
  time: e.time,
  address: e.address,
  requiredPoints: e.requiredPoints,
  totalBoxes: e.totalBoxes,
  boxesLeft: e.boxesLeft,
  active,
  // Custom questions are persisted as a JSON string in a single sheet column.
  questions: JSON.stringify(e.customQuestions ?? []),
  products: JSON.stringify(e.products ?? []),
  // The availability master switch rides inside the targeting bag so it persists
  // without a schema change (parseTargeting preserves arbitrary keys on read).
  targeting: JSON.stringify({ ...(e.targeting ?? {}), enabled: e.enabled !== false }),
  recurrence: JSON.stringify(normalizeRecurrence(e.recurrence)),
  brandLogo: e.brandLogo ?? '',
  instagramUrl: e.instagramUrl ?? '',
  websiteUrl: e.websiteUrl ?? '',
})

const generateCode = (length: number) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * Generate a unique, human-readable drop code (e.g. "DROP-7K2M"). This is the
 * code a drop "gets named" — admins enter it to look up the drop's exact date,
 * brand and participants.
 */
export const generateDropCode = () => `DROP-${generateCode(4)}`

/** Normalize a drop code for comparison (case-insensitive, no spaces). */
export const normalizeCode = (code: string) =>
  code.replace(/\s/g, '').toUpperCase()

/**
 * Find the drop (Event) whose admin-created code matches `code`. This is the
 * gate for the consumer app — only real admin drop codes grant entry / redeem
 * a reserved spot. Returns undefined when no drop matches.
 */
export const findEventByCode = (
  events: Event[],
  code: string,
): Event | undefined => {
  const target = normalizeCode(code)
  if (!target) return undefined
  return events.find((e) => e.code && normalizeCode(e.code) === target)
}

/** Map a backend drop row (FetchedDrop shape) into a store Event. */
const fetchedDropToEvent = (d: any): Event => ({
  id: d.id,
  code: d.code,
  title: d.title,
  brand: d.brand,
  campaign: d.campaign || d.title,
  verificationMethod: normalizeVerificationMethod(d.verificationMethod),
  description: d.description,
  date: d.date,
  time: d.time,
  address: d.address,
  boxesLeft: d.boxesLeft,
  totalBoxes: d.totalBoxes,
  theme: 'Drop',
  type: 'reservation',
  recurrence: normalizeRecurrence(d.recurrence),
  requiredPoints: d.requiredPoints,
  enabled: ((d.targeting as any)?.enabled ?? true) !== false,
  targeting: (() => {
    const { enabled: _e, ...rest } = (d.targeting as any) ?? {}
    return rest as DropTargeting
  })(),
  customQuestions: d.customQuestions,
  products: d.products,
  brandLogo: d.brandLogo,
  instagramUrl: d.instagramUrl,
  websiteUrl: d.websiteUrl,
})

/** A user "took part" in a drop once they actually caught it (redeemed/reviewed). */
const participatedIn = (
  reservations: Reservation[],
  eventId: string,
): boolean =>
  reservations.some(
    (r) =>
      r.eventId === eventId &&
      (r.status === 'redeemed' || r.status === 'reviewed'),
  )

/**
 * Whether THIS person has already collected (redeemed/reviewed) a given drop.
 * A drop code can only be collected once per registered person — every later
 * attempt for the same drop is blocked. `userId` is the person's phone.
 */
export const hasCollectedDrop = (
  reservations: Reservation[],
  eventId: string,
  userId?: string,
): boolean =>
  reservations.some(
    (r) =>
      r.eventId === eventId &&
      (!userId || r.userId === userId) &&
      (r.status === 'redeemed' || r.status === 'reviewed'),
  )

/** Pull "HH:MM" pairs out of a free-form time string (e.g. "12:00 - 14:00"). */
const parseDropTimes = (
  time: string,
): { start?: [number, number]; end?: [number, number] } => {
  const matches = (time || '').match(/\d{1,2}:\d{2}/g) || []
  const toPair = (s?: string): [number, number] | undefined => {
    if (!s) return undefined
    const [h, m] = s.split(':').map(Number)
    return [h || 0, m || 0]
  }
  return { start: toPair(matches[0]), end: toPair(matches[1]) }
}

/**
 * Result of checking whether a drop is collectable RIGHT NOW. A drop is only
 * active during the exact date + time window set in the admin — never before
 * the start, never after the end. When no time is configured the whole day is
 * treated as the window; when no end time is given we default to a 4-hour run.
 */
export type DropWindowStatus = 'active' | 'before' | 'after' | 'unknown' | 'off'

/** Whether the drop's master availability switch is on (undefined = on). */
export const isDropEnabled = (event: Pick<Event, 'enabled'>): boolean =>
  event.enabled !== false

/** Format a Date as a local YYYY-MM-DD string. */
export const toISODate = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * The end date (YYYY-MM-DD) for a "rolling" availability that starts today and
 * runs for `weeks` weeks — used by the admin's one-click "roll on for N weeks".
 */
export const rollingUntil = (weeks: number, from: Date = new Date()): string => {
  const end = new Date(from)
  end.setDate(end.getDate() + Math.max(1, Math.round(weeks)) * 7)
  return toISODate(end)
}

export const getDropWindowStatus = (
  event: Pick<Event, 'date' | 'time' | 'recurrence' | 'enabled'>,
  now: Date = new Date(),
): DropWindowStatus => {
  // A manually turned-off drop is never available, whatever its schedule says.
  if (event.enabled === false) return 'off'
  // Available + no scheduled date = live RIGHT NOW, indefinitely. Admins only
  // set a date/time when they want to constrain the window; otherwise "ON"
  // means immediately and always collectable.
  if (!event.date) return 'active'
  // For a recurring drop, evaluate the window against the next occurrence on or
  // after today (so a weekly/daily/monthly drop reopens on each occurrence day)
  // rather than only its original start date.
  let effectiveDate = event.date
  if (isRecurring(normalizeRecurrence(event.recurrence))) {
    const occ = nextOccurrence(event, now)
    if (occ) effectiveDate = occ
  }
  const day = new Date(`${effectiveDate}T00:00:00`)
  if (Number.isNaN(day.getTime())) return 'unknown'

  const { start, end } = parseDropTimes(event.time)
  const startDate = new Date(day)
  if (start) startDate.setHours(start[0], start[1], 0, 0)
  else startDate.setHours(0, 0, 0, 0)

  const endDate = new Date(day)
  if (end) endDate.setHours(end[0], end[1], 0, 0)
  else if (start) endDate.setHours(start[0] + 4, start[1], 0, 0)
  else endDate.setHours(23, 59, 59, 999)

  const t = now.getTime()
  if (t < startDate.getTime()) return 'before'
  if (t > endDate.getTime()) return 'after'
  return 'active'
}

/** Convenience: is the drop collectable right now (within its admin window). */
export const isDropActiveNow = (
  event: Pick<Event, 'date' | 'time' | 'recurrence' | 'enabled'>,
  now: Date = new Date(),
): boolean => getDropWindowStatus(event, now) === 'active'

/**
 * Sequential-drop gating: a person may only reserve a drop if they took part in
 * the drop immediately before it. Drops are ordered by date (then code as a
 * stable tiebreaker). The FIRST drop is always open; every later drop requires
 * having caught (redeemed or reviewed) the one before it. Drops the user has
 * already reserved are excluded from "needs unlocking" by the caller.
 */
export const isDropUnlockedBySequence = (
  events: Event[],
  reservations: Reservation[],
  eventId: string,
): boolean => {
  const ordered = [...events].sort(
    (a, b) => a.date.localeCompare(b.date) || a.code.localeCompare(b.code),
  )
  const idx = ordered.findIndex((e) => e.id === eventId)
  if (idx <= 0) return true // unknown drop or the very first drop → open
  const previous = ordered[idx - 1]
  return participatedIn(reservations, previous.id)
}

/**
 * True when the user is holding a reserved-but-not-yet-redeemed spot. They must
 * redeem (pick up) that drop before reserving anything new — only ONE active
 * reservation is allowed at a time.
 */
export const hasActiveReservation = (reservations: Reservation[]): boolean =>
  reservations.some((r) => r.status === 'reserved')

/**
 * True when the user has a redeemed catch still awaiting its review. They must
 * review it before they can reserve the next drop.
 */
export const hasPendingReview = (reservations: Reservation[]): boolean =>
  reservations.some((r) => r.status === 'redeemed')

/**
 * The single gate for reserving a NEW drop. The lifecycle is strictly:
 *   catch (redeem) → review → reserve one upcoming drop → redeem → repeat.
 * A user may reserve again ONLY when they hold no active reservation AND have
 * no redeemed catch waiting to be reviewed. This enforces "one reservation per
 * user at a time", re-opened after every redeemed-then-reviewed catch.
 */
export const canReserveNewDrop = (reservations: Reservation[]): boolean =>
  !hasActiveReservation(reservations) && !hasPendingReview(reservations)

/**
 * Friend invitations unlock ONLY after a user has completed their first full
 * loop: a successful redemption AND a review of it. A reservation reaches
 * status 'reviewed' only after it was redeemed and then reviewed, so the
 * presence of any 'reviewed' reservation proves both happened. This gates the
 * invite affordance in the Friends section.
 */
export const hasUnlockedFriendInvites = (
  reservations: Reservation[],
): boolean => reservations.some((r) => r.status === 'reviewed')

/** Build the SheetDB user payload from a User + their catch count. */
const sheetUserPayload = (u: User, catchCount: number) => ({
  name: u.name,
  phone: u.phone,
  ageRange: u.ageRange,
  city: u.city,
  occupation: u.occupation,
  profession: u.profession,
  workArea: u.workArea,
  favoriteArea: u.favoriteArea,
  interests: u.interests,
  storePreference: u.storePreference,
  customAnswers: u.customAnswers,
  invitedBy: u.invitedBy,
  partnerCode: u.partnerCode,
  points: u.points,
  level: u.level,
  catchCount,
})

/**
 * Award points to the current user: recompute their playful level, persist the
 * new total to Supabase (so points are SAVED across devices, not just held in
 * memory), and keep the local registeredUsers record in sync. Returns the slice
 * of state to merge. No-op when there's no signed-in user.
 */
function applyPoints(
  state: AppState,
  delta: number,
  extraUserFields: Partial<User> = {},
): Partial<AppState> {
  if (!state.user) return {}
  const points = Math.max(0, state.user.points + delta)
  const level = levelForPoints(points)
  const updatedUser: User = { ...state.user, ...extraUserFields, points, level }
  const phone = normalizePhone(updatedUser.phone)
  const reg = state.registeredUsers.find(
    (u) => normalizePhone(u.phone) === phone,
  )
  const catchCount = reg?.catchCount ?? 1
  // Persist the new points + level to Supabase (fire-and-forget). The admin
  // snapshot folds rows by phone taking the MAX points, so appending the latest
  // total is enough to keep the saved score correct on every device.
  syncUser(sheetUserPayload(updatedUser, catchCount))
  const registeredUsers = reg
    ? state.registeredUsers.map((u) =>
        normalizePhone(u.phone) === phone
          ? { ...u, ...extraUserFields, points, level }
          : u,
      )
    : state.registeredUsers
  return { user: updatedUser, registeredUsers }
}

// No mock/seed drops. Admins create real drops in the Admin → Drops tab; they
// flow into the user app through this shared persisted store.
const DEFAULT_EVENTS: Event[] = []

const INITIAL_STATE = {
  user: null,
  registeredUsers: [] as RegisteredUser[],
  // No drop is active until an admin creates one.
  activeEventId: null as string | null,
  activeBatchCode: BATCH_CODES[0].code,
  dropWriteError: null as string | null,
  deletedDropIds: [] as string[],
  events: DEFAULT_EVENTS,
  reservations: [] as Reservation[],
  reviews: [] as ReviewEntry[],
  recurringQuestions: [] as DropQuestion[],
  partnerCodes: [] as PartnerCode[],
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      setUser: (user, isReturning = false) =>
        set((state) => {
          const now = Date.now()
          const incomingPhone = normalizePhone(user.phone)
          const existing = state.registeredUsers.find(
            (u) => normalizePhone(u.phone) === incomingPhone,
          )
          // New-vs-returning is driven by the user's own choice on the entry
          // screen (phone-number identification was unreliable). If we DO happen
          // to match an existing phone, keep incrementing their count; otherwise
          // a self-declared returning visitor starts at 2 (so they read as
          // "returning"), and a first-timer starts at 1.
          const catchCount = existing
            ? existing.catchCount + 1
            : isReturning
              ? 2
              : 1
          const mergedPoints = user.points ?? existing?.points ?? 0
          const mergedUser: User = {
            ...existing,
            ...user,
            // The referrer is captured once, on first registration, and never
            // overwritten on later catches.
            invitedBy: existing?.invitedBy ?? user.invitedBy,
            // Same for the partner code that attributed this sign-up.
            partnerCode: existing?.partnerCode ?? user.partnerCode,
            // Level is always derived from the saved points total.
            points: mergedPoints,
            level: levelForPoints(mergedPoints),
            catchCount,
          }
          const registeredUser: RegisteredUser = {
            ...mergedUser,
            registeredAt: existing?.registeredAt || now,
            lastSeenAt: now,
            catchCount,
          }
          // Live sync to Supabase (fire-and-forget).
          syncUser({
            name: registeredUser.name,
            phone: registeredUser.phone,
            ageRange: registeredUser.ageRange,
            city: registeredUser.city,
            occupation: registeredUser.occupation,
            profession: registeredUser.profession,
            workArea: registeredUser.workArea,
            favoriteArea: registeredUser.favoriteArea,
            interests: registeredUser.interests,
            storePreference: registeredUser.storePreference,
            customAnswers: registeredUser.customAnswers,
            invitedBy: registeredUser.invitedBy,
            partnerCode: registeredUser.partnerCode,
            points: registeredUser.points,
            level: registeredUser.level,
            catchCount,
          })
          // NOTE: Supabase is the single source of truth — there is no Google
          // Sheets sync. All persistence (above and below) goes to Supabase.
          return {
            user: mergedUser,
            registeredUsers: existing
              ? state.registeredUsers.map((u) =>
                  normalizePhone(u.phone) === incomingPhone
                    ? registeredUser
                    : u,
                )
              : [...state.registeredUsers, registeredUser],
          }
        }),
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
      followInstagram: () => {
        const state = useStore.getState()
        if (!state.user) return false
        // Already claimed → no double-dipping.
        if (state.user.customAnswers?.[IG_FOLLOWED_KEY] === 'true') return false
        const customAnswers = {
          ...(state.user.customAnswers || {}),
          [IG_FOLLOWED_KEY]: 'true',
        }
        // applyPoints awards + PERSISTS the points (recomputes level, syncs to
        // Supabase) and folds the follow flag into the saved customAnswers.
        set((s) => applyPoints(s, INSTAGRAM_FOLLOW_POINTS, { customAnswers }))
        return true
      },
      setNotificationOptIn: (optIn) =>
        set((state) => {
          if (!state.user) return {}
          const customAnswers = {
            ...(state.user.customAnswers || {}),
            // Empty string clears the flag on the backend (asRecord drops it).
            [NOTIFY_OPTIN_KEY]: optIn ? 'true' : '',
          }
          const updatedUser: User = { ...state.user, customAnswers }
          const phone = normalizePhone(updatedUser.phone)
          const reg = state.registeredUsers.find(
            (u) => normalizePhone(u.phone) === phone,
          )
          // Persist the preference (fire-and-forget) so it survives across devices.
          syncUser(sheetUserPayload(updatedUser, reg?.catchCount ?? 1))
          return {
            user: updatedUser,
            registeredUsers: state.registeredUsers.map((u) =>
              normalizePhone(u.phone) === phone ? { ...u, customAnswers } : u,
            ),
          }
        }),
      setActiveEventId: (id) => set({ activeEventId: id }),
      setActiveBatchCode: (code) => set({ activeBatchCode: code }),
      addReservation: (eventId, status = 'reserved', isRecurring = false) =>
        set((state) => {
          const newReservation: Reservation = {
            id: Math.random().toString(36).substr(2, 9),
            eventId,
            userId: state.user?.phone || 'unknown',
            status: status,
            timestamp: Date.now(),
            userCode: generateCode(4),
            staffCode: generateCode(4),
            batchCode: state.activeBatchCode,
            isRecurring,
          }
          const event = state.events.find((e) => e.id === eventId)
          const round =
            BATCH_CODES.find((b) => b.code === newReservation.batchCode)
              ?.round ?? ''
          // Persist to Supabase (single source of truth, fire-and-forget).
          syncCatch({
            reservationId: newReservation.id,
            userPhone: newReservation.userId,
            event: event?.title || '',
            brand: event?.brand || '',
            batchCode: newReservation.batchCode,
            round,
            status: newReservation.status,
            userCode: newReservation.userCode,
            staffCode: newReservation.staffCode,
            recurring: isRecurring,
          })
          // Decrement the drop's remaining boxes on the shared backend too, so
          // availability stays in sync across every device.
          if (event) {
            patchDrop(eventId, { boxesLeft: Math.max(0, event.boxesLeft - 1) })
          }
          return {
            reservations: [...state.reservations, newReservation],
            events: state.events.map((e) =>
              e.id === eventId ? { ...e, boxesLeft: e.boxesLeft - 1 } : e,
            ),
            // Award + PERSIST points (recomputes level, syncs to Supabase).
            ...applyPoints(state, status === 'redeemed' ? 20 : 10),
          }
        }),
      redeemReservation: (reservationId) =>
        set((state) => {
          patchCatch(reservationId, { status: 'redeemed' })
          return {
            reservations: state.reservations.map((r) =>
              r.id === reservationId ? { ...r, status: 'redeemed' } : r,
            ),
            ...applyPoints(state, 20),
          }
        }),
      redeemReservationByCode: (reservationId, code) => {
        const state = useStore.getState()
        const reservation = state.reservations.find(
          (r) => r.id === reservationId,
        )
        if (!reservation) return false
        const event = state.events.find((e) => e.id === reservation.eventId)
        // Pickup conversion is gated by the drop's real admin code. The code a
        // returning user is handed at pickup IS the general drop code — we just
        // tag the resulting catch as recurring so the platform can tell a
        // reserved-spot pickup apart from a fresh walk-in.
        if (!event || !event.code) return false
        // Respect the drop's verification method: a QR-only drop cannot be
        // redeemed by typing the drop code (the user must scan).
        if (!allowsCodeRedemption(event.verificationMethod)) return false
        if (normalizeCode(code) !== normalizeCode(event.code)) return false
        // Only collectable during the drop's admin-set date + time window.
        if (!isDropActiveNow(event)) return false
        // A person can only collect a given drop ONCE — block repeats.
        if (
          hasCollectedDrop(state.reservations, event.id, reservation.userId) &&
          reservation.status !== 'reserved'
        )
          return false
        patchCatch(reservationId, { status: 'redeemed', recurring: true })
        set((s) => ({
          reservations: s.reservations.map((r) =>
            r.id === reservationId
              ? { ...r, status: 'redeemed', isRecurring: true }
              : r,
          ),
          ...applyPoints(s, 20),
        }))
        return true
      },
      completeReview: (reservationId) =>
        set((state) => {
          patchCatch(reservationId, { status: 'reviewed' })
          return {
            reservations: state.reservations.map((r) =>
              r.id === reservationId ? { ...r, status: 'reviewed' } : r,
            ),
            ...applyPoints(state, 30, { eligible_for_next_event: true }),
          }
        }),
      addReviewEntry: (entry) =>
        set((state) => {
          const newReview: ReviewEntry = {
            ...entry,
            id: Math.random().toString(36).substr(2, 9),
            timestamp: Date.now(),
          }
          // Persist to Supabase (single source of truth, fire-and-forget).
          syncReview({
            reviewId: newReview.id,
            reservationId: newReview.reservationId,
            userPhone: newReview.userId,
            product: newReview.productName,
            brand: newReview.productBrand,
            taste: newReview.taste,
            packaging: newReview.packaging,
            buyAgain: newReview.buyAgain,
          })
          return {
            reviews: [...state.reviews, newReview],
          }
        }),
      clearDropWriteError: () => set({ dropWriteError: null }),
      addEvent: (event) =>
        set((state) => {
          // Push the new drop to the shared backend so it appears on every
          // device. If the write is REJECTED (e.g. RLS blocks anon writes) the
          // drop won't persist across refresh/devices — surface that clearly
          // instead of silently losing it. The drop stays in local state so it
          // is usable this session.
          syncDrop(
            eventToSheetDrop(event, event.id === state.activeEventId),
          ).then((ok) => {
            if (!ok)
              set({
                dropWriteError:
                  'This drop was saved locally but the database rejected the write, so it won’t persist across refresh or other devices. Fix write access in Supabase (run the RLS setup SQL in lib/supabase.ts, or disable RLS on the drops table).',
              })
          })
          // Tell every other open surface (user/brand/partner) to refresh now.
          notifyDataChanged('drops')
          // A newly created drop clears any tombstone with the same id.
          return {
            events: [...state.events, event],
            deletedDropIds: state.deletedDropIds.filter((x) => x !== event.id),
          }
        }),
      updateEvent: (id, updates) =>
        set((state) => {
          const next = state.events.map((e) =>
            e.id === id ? { ...e, ...updates } : e,
          )
          const updated = next.find((e) => e.id === id)
          if (updated)
            patchDrop(
              id,
              eventToSheetDrop(updated, id === state.activeEventId),
            )
          notifyDataChanged('drops')
          return { events: next }
        }),
      refillDrop: (id, amount) =>
        set((state) => {
          const add = Math.max(0, Math.round(amount))
          if (add === 0) return {}
          const next = state.events.map((e) =>
            e.id === id
              ? {
                  ...e,
                  totalBoxes: Math.max(0, e.totalBoxes + add),
                  boxesLeft: Math.max(0, e.boxesLeft + add),
                }
              : e,
          )
          const updated = next.find((e) => e.id === id)
          if (updated)
            patchDrop(id, {
              totalBoxes: updated.totalBoxes,
              boxesLeft: updated.boxesLeft,
            })
          notifyDataChanged('drops')
          return { events: next }
        }),
      deleteEvent: (id) =>
        set((state) => {
          // Tombstone the id so a delete that fails to persist server-side does
          // NOT resurrect on the next hydrate within this session.
          deleteDropRow(id).then((ok) => {
            if (!ok)
              set({
                dropWriteError:
                  'This drop was removed locally but the database rejected the delete, so it may reappear on other devices. Fix write access in Supabase (run the RLS setup SQL in lib/supabase.ts, or disable RLS on the drops table).',
              })
          })
          notifyDataChanged('drops')
          return {
            events: state.events.filter((e) => e.id !== id),
            deletedDropIds: state.deletedDropIds.includes(id)
              ? state.deletedDropIds
              : [...state.deletedDropIds, id],
          }
        }),
      setEvents: (events) => set({ events }),
      hydrateDrops: async () => {
        const drops = await fetchDrops()
        // null = fetch failed: keep last-known drops rather than wiping them.
        if (!drops) return
        set((state) => {
          const active =
            drops.find((d) => d.active)?.id ?? state.activeEventId ?? null
          // Drop any rows the user deleted this session (tombstones) so a
          // delete that failed to persist server-side doesn't come back.
          const fetched = drops
            .filter((d) => !state.deletedDropIds.includes(d.id))
            .map((d) => ({
              id: d.id,
              code: d.code,
              title: d.title,
              brand: d.brand,
              campaign: d.campaign || d.title,
              verificationMethod: normalizeVerificationMethod(
                d.verificationMethod,
              ),
              description: d.description,
              date: d.date,
              time: d.time,
              address: d.address,
              boxesLeft: d.boxesLeft,
              totalBoxes: d.totalBoxes,
              theme: 'Drop',
              type: 'reservation' as const,
              recurrence: normalizeRecurrence(d.recurrence),
              requiredPoints: d.requiredPoints,
              // Pull the availability switch back out of the targeting bag and
              // keep the rest of the targeting object clean.
              enabled: ((d.targeting as any)?.enabled ?? true) !== false,
              targeting: (() => {
                const { enabled: _e, ...rest } = (d.targeting as any) ?? {}
                return rest as DropTargeting
              })(),
              customQuestions: d.customQuestions,
              products: d.products,
              brandLogo: d.brandLogo,
              instagramUrl: d.instagramUrl,
              websiteUrl: d.websiteUrl,
            }))
          // Preserve locally-created drops that aren't in the backend yet (e.g.
          // when the write was rejected) so they don't vanish on refresh.
          const fetchedIds = new Set(fetched.map((d) => d.id))
          const localOnly = state.events.filter(
            (e) => !fetchedIds.has(e.id) && !state.deletedDropIds.includes(e.id),
          )
          return {
            events: [...fetched, ...localOnly],
            activeEventId: active,
          }
        })
      },
      resolveDropByCode: async (code) => {
        // 1) Already in the local list (created here, persisted, or synced)?
        const local = findEventByCode(useStore.getState().events, code)
        if (local) return local
        // 2) Live backend read by code — reads work even when writes are denied.
        const drop = await fetchDropByCode(code)
        if (!drop) return null
        const ev = fetchedDropToEvent(drop)
        // Merge it in (dedupe by id) so the rest of the flow can use it.
        set((state) => {
          if (state.events.some((e) => e.id === ev.id)) return {}
          return { events: [...state.events, ev] }
        })
        return ev
      },
      setRecurringQuestions: (questions) => {
        // Cap at 3 — recurring users get up to 3 extra questions.
        const capped = questions.slice(0, 3)
        syncRecurringQuestions(capped)
        notifyDataChanged('questions')
        set({ recurringQuestions: capped })
      },
      hydrateRecurringQuestions: async () => {
        const questions = await fetchRecurringQuestions()
        // null = fetch failed: keep last-known config rather than wiping it.
        if (!questions) return
        set({ recurringQuestions: questions.slice(0, 3) })
      },
      setPartnerCodes: (codes) => {
        syncPartnerCodes(codes)
        notifyDataChanged('partnerCodes')
        set({ partnerCodes: codes })
      },
      hydratePartnerCodes: async () => {
        const codes = await fetchPartnerCodes()
        // null = fetch failed: keep last-known codes rather than wiping them.
        if (!codes) return
        set({ partnerCodes: codes })
      },
      hydrateUserReservations: async () => {
        const state = useStore.getState()
        if (!state.user?.phone) return
        const sheetCatches = await fetchUserCatches(state.user.phone)
        if (!sheetCatches || sheetCatches.length === 0) return

        set((currentState) => {
          let changed = false
          const updatedReservations = currentState.reservations.map(
            (localRes) => {
              const sheetMatch = sheetCatches.find(
                (c) => c.reservationId === localRes.id,
              )
              if (sheetMatch && sheetMatch.status !== localRes.status) {
                changed = true
                return { ...localRes, status: sheetMatch.status as any }
              }
              return localRes
            },
          )

          return changed ? { reservations: updatedReservations } : {}
        })
      },
      hydrateUserByPhone: async (phone) => {
        const saved = await fetchUserProfile(phone)
        if (!saved) return // no saved profile in Supabase (or fetch failed)
        const { user: sheetUser, catches, reviews } = saved

        // Rebuild this user's reservations from their saved catches so My
        // Catches, points gating and the lifecycle work on a fresh device.
        const rebuiltReservations: Reservation[] = catches
          .filter((c) => c.id)
          .map((c) => {
            const event = useStore
              .getState()
              .events.find(
                (e) =>
                  e.title === c.eventTitle ||
                  normalizeCode(e.brand) === normalizeCode(c.eventBrand),
              )
            const status = (
              ['reserved', 'redeemed', 'reviewed', 'expired'].includes(c.status)
                ? c.status
                : 'reserved'
            ) as Reservation['status']
            return {
              id: c.id,
              eventId: event?.id || c.eventTitle,
              userId: sheetUser.phone,
              status,
              timestamp: c.timestamp || Date.now(),
              userCode: c.userCode,
              staffCode: c.staffCode,
              batchCode: c.batchCode,
              isRecurring: c.recurring,
            }
          })

        const rebuiltReviews: ReviewEntry[] = reviews
          .filter((r) => r.id)
          .map((r) => ({
            id: r.id,
            reservationId: r.reservationId,
            userId: sheetUser.phone,
            productName: r.productName,
            productBrand: r.productBrand,
            taste: r.taste,
            packaging: r.packaging,
            buyAgain: r.buyAgain,
            timestamp: r.timestamp || Date.now(),
          }))

        set((state) => {
          // Merge sheet reservations with any local-only ones (by id).
          const localOnly = state.reservations.filter(
            (lr) => !rebuiltReservations.some((sr) => sr.id === lr.id),
          )
          const mergedReservations = [...rebuiltReservations, ...localOnly]
          const localReviews = state.reviews.filter(
            (lr) => !rebuiltReviews.some((sr) => sr.id === lr.id),
          )
          const mergedReviews = [...rebuiltReviews, ...localReviews]

          // Restore the SAVED points/level (sheet is the source of truth on
          // login). Never let a fresh device's 0 overwrite the saved score.
          const phoneKey = normalizePhone(sheetUser.phone)
          const points = Math.max(
            sheetUser.points || 0,
            state.user && normalizePhone(state.user.phone) === phoneKey
              ? state.user.points
              : 0,
          )
          const level = levelForPoints(points)
          const restoredUser: User | null = state.user
            ? {
                ...state.user,
                points,
                level,
                eligible_for_next_event:
                  sheetUser.eligible_for_next_event ||
                  state.user.eligible_for_next_event,
              }
            : state.user

          const registeredUsers = state.registeredUsers.map((u) =>
            normalizePhone(u.phone) === phoneKey
              ? {
                  ...u,
                  points,
                  level,
                  catchCount: Math.max(u.catchCount, sheetUser.catchCount),
                }
              : u,
          )

          return {
            reservations: mergedReservations,
            reviews: mergedReviews,
            user: restoredUser,
            registeredUsers,
          }
        })
      },
      resetStore: () => set(INITIAL_STATE),
    }),
    {
      name: 'finns-i-sjon-storage',
      // v6: added partner codes (brand/event attribution) + user.partnerCode.
      // v7: playful level names — re-derive every persisted level from points.
      version: 7,
      migrate: (persisted: any, version) => {
        if (!persisted) return persisted
        if (version < 7) {
          if (persisted.user) {
            persisted.user.level = levelForPoints(persisted.user.points ?? 0)
          }
          if (Array.isArray(persisted.registeredUsers)) {
            persisted.registeredUsers = persisted.registeredUsers.map(
              (u: any) => ({ ...u, level: levelForPoints(u.points ?? 0) }),
            )
          }
        }
        return persisted
      },
    },
  ),
)
